import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { api, formatApiErrorDetail } from "../lib/api";
import { Info, CheckCircle, LinkedinLogo, Sparkle } from "@phosphor-icons/react";

/**
 * Read-only coach view of a coachee's LinkedIn step-plan. The plan is one
 * long markdown blob generated by the AI; we just render it.
 */
export default function LinkedinPlanCoachPanel({ coacheeId, coacheeName }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get(`/coachees/${coacheeId}/linkedin-plan`);
        if (!cancelled) setDoc(data);
      } catch (err) {
        if (!cancelled) setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [coacheeId]);

  const plan = (doc?.plan || "").trim();
  const aiCount = doc?.ai_count ?? 0;
  const status = doc?.status || "idle";

  return (
    <div className="space-y-4 max-w-3xl" data-testid="linkedin-plan-coach-panel">
      <div className="neuro-card-bold p-5 bg-white">
        <div className="flex items-start gap-3">
          <LinkedinLogo size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold tracking-tight">LinkedIn-stappenplan</h3>
            <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
              AI-gegenereerd plan met concrete stappen om {coacheeName || "de coachee"} sterker
              zichtbaar te maken op LinkedIn.
            </p>
            <div className="mt-3 flex items-center gap-4 text-xs text-zinc-500 flex-wrap" data-testid="linkedin-plan-coach-stats">
              <span>
                Status: <strong className="text-zinc-900">
                  {plan ? "Plan beschikbaar" : status === "running" ? "Wordt gegenereerd…" : "Nog niet gegenereerd"}
                </strong>
              </span>
              {aiCount > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Sparkle size={12} className="text-[#1E88E5]" /> {aiCount} × gegenereerd
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm" data-testid="linkedin-plan-coach-error">{error}</div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm">Laden...</div>
      ) : !plan ? (
        <div className="neuro-card p-6 bg-white" data-testid="linkedin-plan-coach-empty">
          <div className="flex items-start gap-3">
            <Info size={22} className="text-[#1E88E5] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-display text-base font-bold tracking-tight">
                {status === "running" ? "Wordt gegenereerd…" : "Nog niet gegenereerd"}
              </h4>
              <p className="text-sm text-zinc-600 mt-1">
                {status === "running"
                  ? "De AI is bezig — kijk over een minuutje opnieuw."
                  : `${coacheeName || "De coachee"} heeft het LinkedIn-stappenplan nog niet aangevraagd.`}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="neuro-card p-6 bg-white" data-testid="linkedin-plan-coach-content">
          <div className="flex items-start gap-3 mb-3">
            <CheckCircle size={20} className="text-[#43A047] shrink-0 mt-0.5" />
            <h4 className="font-display text-base font-bold tracking-tight">Plan</h4>
          </div>
          <div className="prose prose-sm max-w-none prose-zinc prose-headings:font-display prose-headings:tracking-tight">
            <ReactMarkdown>{plan}</ReactMarkdown>
          </div>
          {doc?.generated_at && (
            <div className="mt-4 text-xs text-zinc-400">
              Gegenereerd op {new Date(doc.generated_at).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
